// Hotkey serialisation + matching helpers shared by the global
// binder, the palette, the in-CI conflict lint, and the keyboard-
// shortcuts page.
//
// The canonical hotkey string is platform-agnostic ("mod+shift+r")
// and lower-cased; renderToParts() turns it into the platform-
// appropriate display ("⌘⇧R" on macOS, "Ctrl+Shift+R" elsewhere).
import type { Hotkey, ModKey } from "@/lib/commands/types";

const MOD_ORDER: ModKey[] = ["mod", "ctrl", "cmd", "alt", "shift"];

export function isMac(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPod|iPad/i.test(navigator.platform || navigator.userAgent);
}

// canonicalKey returns a stable string used as the lookup key in
// conflict detection. "mod" is preserved verbatim — two hotkeys
// that both say "mod+k" conflict on every platform; one with "cmd"
// and one with "ctrl" don't conflict statically but might at
// runtime on a single platform. The runtime warn-on-conflict
// handler in <CommandHotkeys/> handles that case.
export function canonicalKey(hk: Hotkey): string {
  const mods = (hk.mod ?? [])
    .slice()
    .sort((a, b) => MOD_ORDER.indexOf(a) - MOD_ORDER.indexOf(b));
  return [...mods, hk.key.toLowerCase()].join("+");
}

// renderToParts produces the visible shortcut chip pieces for a
// hotkey, e.g. ["⌘", "⇧", "R"] on macOS or ["Ctrl", "Shift", "R"]
// elsewhere. The palette right-aligns this; the cheatsheet inlines
// it.
export function renderToParts(hk: Hotkey): string[] {
  const mac = isMac();
  const parts: string[] = [];
  for (const m of hk.mod ?? []) {
    if (m === "mod" || m === "cmd") parts.push(mac ? "⌘" : "Ctrl");
    else if (m === "ctrl") parts.push(mac ? "⌃" : "Ctrl");
    else if (m === "alt") parts.push(mac ? "⌥" : "Alt");
    else if (m === "shift") parts.push(mac ? "⇧" : "Shift");
  }
  parts.push(hk.key.length === 1 ? hk.key.toUpperCase() : hk.key);
  return parts;
}

// matchEvent decides whether a keyboard event satisfies a hotkey.
// "mod" maps to ⌘ on macOS and Ctrl elsewhere. We deliberately do
// NOT match when the user is typing inside an input/textarea/
// contenteditable — the palette's own input is the one exception
// and is handled by the in-palette key dispatcher.
export function matchEvent(hk: Hotkey, e: KeyboardEvent): boolean {
  if (e.key.toLowerCase() !== hk.key.toLowerCase()) return false;
  const mods = hk.mod ?? [];
  const mac = isMac();
  const wantsCmd = mods.includes("cmd") || (mods.includes("mod") && mac);
  const wantsCtrl = mods.includes("ctrl") || (mods.includes("mod") && !mac);
  const wantsAlt = mods.includes("alt");
  const wantsShift = mods.includes("shift");
  if (Boolean(e.metaKey) !== wantsCmd) return false;
  if (Boolean(e.ctrlKey) !== wantsCtrl) return false;
  if (Boolean(e.altKey) !== wantsAlt) return false;
  if (Boolean(e.shiftKey) !== wantsShift) return false;
  return true;
}

export function isTypingInInput(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}
